import Cookies from "js-cookie"

/**
 * Cookie name for storing consent preference.
 * @type {string}
 */
export const CONSENT_COOKIE_NAME = "ciq_cookie_consent"

/**
 * Valid consent values.
 * @type {Array<string>}
 */
const VALID_CONSENT_VALUES = ["accepted", "rejected"]

/**
 * Get the current cookie consent status.
 *
 * @returns {'accepted' | 'rejected' | null} The consent status or null if not set
 */
export function getCookieConsent() {
  const value = Cookies.get(CONSENT_COOKIE_NAME)

  if (!value || !VALID_CONSENT_VALUES.includes(value)) {
    return null
  }

  return value
}

/**
 * Set the cookie consent status.
 * Uses secure flag only on HTTPS to allow localhost development.
 *
 * @param {'accepted' | 'rejected'} value - The consent value to set
 * @throws {Error} If value is not 'accepted' or 'rejected'
 */
export function setCookieConsent(value) {
  if (!VALID_CONSENT_VALUES.includes(value)) {
    throw new Error(`Invalid consent value: ${value}. Must be 'accepted' or 'rejected'.`)
  }

  // Only set secure flag on HTTPS - allows cookie to work on localhost
  const isSecure = typeof window !== "undefined" && window.location.protocol === "https:"

  Cookies.set(CONSENT_COOKIE_NAME, value, {
    expires: 365,
    sameSite: "Lax",
    secure: isSecure,
    path: "/",
  })
}

/**
 * Clear the cookie consent cookie.
 */
export function clearCookieConsent() {
  Cookies.remove(CONSENT_COOKIE_NAME, { path: "/" })
}

/**
 * @typedef {'us' | 'canada' | 'international'} ConsentRegion
 */

/**
 * Detect the user's consent region based on browser locale.
 * Uses conservative approach: defaults to opt-in (international) for legal safety.
 *
 * - US users (en-US locale) see notice-only banner, tracking enabled immediately
 * - Canada users (en-CA locale) see opt-in banner (PIPEDA requires explicit consent)
 * - All others see opt-in banner (GDPR/UK GDPR require explicit consent)
 *
 * This is a client-side heuristic. For stronger compliance, use server-side
 * geolocation via CloudFront/Cloudflare headers.
 *
 * @returns {ConsentRegion} 'us' for high-confidence US, 'canada' for Canada, 'international' for all others
 */
export function detectConsentRegion() {
  const lang = navigator?.language || ""
  const upperLang = lang.toUpperCase()

  // Match any US locale (en-US, es-US, etc.)
  if (upperLang.endsWith("-US")) {
    return "us"
  }

  // Match any Canada locale (en-CA, fr-CA, etc.)
  // PIPEDA requires explicit consent
  if (upperLang.endsWith("-CA")) {
    return "canada"
  }

  // Default to international (opt-in) for legal safety
  return "international"
}

/**
 * Check if Global Privacy Control (GPC) signal is enabled.
 * GPC is a browser setting that signals opt-out preference.
 * Required to honor under California CPRA and other privacy laws.
 *
 * @returns {boolean} True if GPC is enabled
 */
export function isGlobalPrivacyControlEnabled() {
  return navigator?.globalPrivacyControl === true
}

/**
 * Check if the user is in the US region.
 * Used for auto-opt-in behavior where US law permits implied consent.
 *
 * @returns {boolean} True if user is detected as being in the US
 */
export function isUSRegion() {
  return detectConsentRegion() === "us"
}
